import { useState } from "react";
import type { Vector } from "@shared/types";
import { maturityColor, mean } from "@shared/scoring";

interface Props {
  vectors: Vector[];
  activeVector: number;
  onSelect: (index: number) => void;
  hasData?: boolean;
}

// oklch(...) → same color with alpha, for translucent polygon fills.
function withAlpha(color: string, alpha: number): string {
  return color.replace(/\)\s*$/, ` / ${alpha})`);
}

const CX = 130;
const CY = 120;
const R = 95;
const M = 34; // viewBox margin so axis labels have room
const VB = { x: -M, y: -M, w: 260 + 2 * M, h: 240 + 2 * M };
const LR = 112; // label radius (just outside the outer ring)

export function Radar({ vectors, activeVector, onSelect, hasData = true }: Props) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [infoHover, setInfoHover] = useState<number | null>(null);
  const [infoPin, setInfoPin] = useState<number | null>(null);

  const n = vectors.length || 5;
  const maxVal = 5;
  const angles = Array.from({ length: n }, (_, i) => (Math.PI * 2 * i) / n - Math.PI / 2);

  // Map a viewBox point → percentage position within the (margin-expanded) plot.
  const pct = (px: number, py: number) => ({
    left: `${((px - VB.x) / VB.w) * 100}%`,
    top: `${((py - VB.y) / VB.h) * 100}%`,
  });

  const rings = [1, 2, 3, 4, 5];
  const benchPts = vectors.map((v, i) => {
    const rr = (R * v.benchmark) / maxVal;
    return [CX + rr * Math.cos(angles[i]), CY + rr * Math.sin(angles[i])];
  });
  const scorePts = vectors.map((v, i) => {
    const rr = (R * v.score) / maxVal;
    return [CX + rr * Math.cos(angles[i]), CY + rr * Math.sin(angles[i])];
  });

  const avgScore = mean(vectors.map((v) => v.score)) ?? 0;
  const currentColor = maturityColor(avgScore);

  const hoveredVec = hovered != null ? vectors.find((v) => v.index === hovered) : null;
  const hoveredPt = hovered != null ? scorePts[vectors.findIndex((v) => v.index === hovered)] : null;

  return (
    <div className="radar-plot">
      <svg viewBox={`${VB.x} ${VB.y} ${VB.w} ${VB.h}`} className="radar-svg-el">
        {rings.map((ring) => {
          const rr = (R * ring) / 5;
          const pts = angles.map((a) => [CX + rr * Math.cos(a), CY + rr * Math.sin(a)]);
          return (
            <g key={ring}>
              <polygon points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke="#e5e7eb" strokeWidth="0.75" />
              <text x={CX + 3} y={CY - rr + 3} fontSize="8" fill="#9ca3af" fontFamily="system-ui, sans-serif">
                {ring}
              </text>
            </g>
          );
        })}
        {angles.map((a, i) => (
          <line key={`axis-${i}`} x1={CX} y1={CY} x2={CX + R * Math.cos(a)} y2={CY + R * Math.sin(a)} stroke="#e5e7eb" strokeWidth="0.75" />
        ))}
        <polygon points={benchPts.map((p) => p.join(",")).join(" ")} fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.8" />
        {hasData && (
          <polygon points={scorePts.map((p) => p.join(",")).join(" ")} fill={withAlpha(currentColor, 0.15)} stroke={currentColor} strokeWidth="2" />
        )}
        {hasData &&
          vectors.map((v, i) => {
            const rr = (R * v.score) / maxVal;
            const x = CX + rr * Math.cos(angles[i]);
            const y = CY + rr * Math.sin(angles[i]);
            const isActive = v.index === activeVector;
            const isHover = v.index === hovered;
            return (
              <g key={v.index}>
                <circle
                  cx={x} cy={y} r={11} fill="transparent" style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHovered(v.index)}
                  onMouseLeave={() => setHovered((h) => (h === v.index ? null : h))}
                  onClick={() => onSelect(v.index)}
                />
                <circle
                  cx={x} cy={y} r={isHover ? 6.5 : isActive ? 5.5 : 4}
                  fill={maturityColor(v.score)} stroke="white" strokeWidth={isHover || isActive ? 2 : 1.5}
                  style={{ pointerEvents: "none" }}
                />
              </g>
            );
          })}
      </svg>

      {!hasData && (
        <div className="radar-empty">
          <span className="radar-empty-dot" />
          Awaiting responses — the radar fills in as participants answer.
        </div>
      )}

      {/* Axis labels at each dimension's outer ray */}
      {vectors.map((v, i) => {
        const a = angles[i];
        const lx = CX + LR * Math.cos(a);
        const ly = CY + LR * Math.sin(a);
        const c = Math.cos(a);
        const side = c > 0.25 ? "right" : c < -0.25 ? "left" : "center";
        const pos = pct(lx, ly);
        const showDesc = infoHover === v.index || infoPin === v.index;
        return (
          <div
            key={v.index}
            className={`radar-axis-label side-${side} ${v.index === activeVector ? "active" : ""}`}
            style={{ left: pos.left, top: pos.top }}
            onMouseEnter={() => setInfoHover(v.index)}
            onMouseLeave={() => setInfoHover((f) => (f === v.index ? null : f))}
          >
            <span className="ral-name" onClick={() => onSelect(v.index)}>{v.shortLabel}</span>
            <button
              className="ral-info"
              aria-label={`About ${v.shortLabel}`}
              onClick={() => setInfoPin((f) => (f === v.index ? null : v.index))}
            >
              i
            </button>
            {showDesc && (
              <div className={`radar-desc desc-${side}`}>
                <div className="radar-desc-title">{v.shortLabel}</div>
                <div className="radar-desc-body">{v.label}</div>
              </div>
            )}
          </div>
        );
      })}

      {/* Dot hover tooltip: short name + score / benchmark */}
      {hoveredVec && hoveredPt && (
        <div className="radar-tip" style={pct(hoveredPt[0], hoveredPt[1])}>
          <div className="radar-tip-name">{hoveredVec.shortLabel}</div>
          <div className="radar-tip-rows">
            <span className="radar-tip-cur" style={{ color: maturityColor(hoveredVec.score) }}>{hoveredVec.score.toFixed(1)}</span>
            <span className="radar-tip-sep">/</span>
            <span className="radar-tip-bm">{hoveredVec.benchmark.toFixed(1)}</span>
          </div>
          <div className="radar-tip-legend">score / benchmark</div>
        </div>
      )}
    </div>
  );
}
